import React from 'react';
import { Search, PenTool, Code2, Rocket, TrendingUp } from 'lucide-react';
import { useScrollReveal } from '@/hooks/useScrollReveal';

const processSteps = [
  {
    id: 'discover',
    number: '01',
    title: 'Discover',
    description: 'Deep-dive into your business model, growth goals, and technical landscape.',
    icon: Search,
  },
  {
    id: 'design',
    number: '02',
    title: 'Strategize',
    description: 'Define ROI targets, architecture, and a roadmap to measurable outcomes.',
    icon: PenTool,
  },
  {
    id: 'build',
    number: '03',
    title: 'Build',
    description: 'Develop scalable systems with weekly demos and rapid iteration cycles.',
    icon: Code2,
  },
  {
    id: 'deploy',
    number: '04',
    title: 'Launch',
    description: 'Go live with confidence — full testing, monitoring, and optimization.',
    icon: Rocket,
  },
  {
    id: 'scale',
    number: '05',
    title: 'Scale',
    description: 'Continuous improvement, growth optimization, and long-term partnership.',
    icon: TrendingUp,
  },
];

export const ProcessSection = ({ onOpenContactModal }) => {
  const sectionRef = useScrollReveal();

  return (
    <section
      id="process"
      ref={sectionRef}
      data-testid="process-section"
      className="relative"
      style={{ backgroundColor: '#FFFFFF' }}
    >
      <div className="kv-container">
        <div className="pt-10 pb-12 lg:pt-12 lg:pb-14">
          {/* Section Header */}
          <div className="kv-reveal text-center max-w-3xl mx-auto mb-6">
            {/* Section Label */}
            <p
              className="text-xs font-semibold uppercase tracking-[0.2em] mb-3"
              style={{ color: '#0A3D91' }}
              data-testid="process-label"
            >
              Our Process
            </p>

            {/* Section Headline */}
            <h2
              className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4"
              style={{ color: '#131B2E', letterSpacing: '-0.025em' }}
              data-testid="process-headline"
            >
              From Vision to Velocity
            </h2>

            {/* Section Description */}
            <p
              className="text-lg"
              style={{ color: '#475569', lineHeight: '1.7' }}
            >
              A proven methodology that delivers measurable results within 90 days — 
              not months of planning and delays.
            </p>
          </div>

          {/* Process Steps - Horizontal Layout */}
          <div 
            className="relative"
            data-testid="process-steps"
          >
            {/* Connecting Line - Desktop Only */}
            <div 
              className="hidden lg:block absolute top-12 left-[10%] right-[10%] h-px"
              style={{ backgroundColor: '#E2E8F0' }}
            />

            {/* Steps Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6 lg:gap-3">
              {processSteps.map((step, index) => (
                <div
                  key={step.id}
                  className="relative flex flex-col items-center text-center group"
                  data-testid={`process-step-${step.id}`}
                >
                  {/* Step Icon Container */}
                  <div className="relative mb-4">
                    {/* Background circle */}
                    <div
                      className="w-16 h-16 rounded-full flex items-center justify-center"
                      style={{ 
                        backgroundColor: '#FFFFFF',
                        border: '2px solid #E2E8F0',
                        boxShadow: 'var(--kv-shadow-sm)',
                        transition: 'box-shadow 0.3s cubic-bezier(0.25,0.1,0.25,1), border-color 0.3s cubic-bezier(0.25,0.1,0.25,1)',
                      }}
                    >
                      {/* Icon */}
                      <step.icon
                        className="w-6 h-6 transition-colors duration-300"
                        style={{ color: '#0A3D91' }}
                      />
                    </div>
                    
                    {/* Step Number Badge */}
                    <div
                      className="absolute -top-1 -right-1 w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold"
                      style={{ 
                        backgroundColor: '#0A3D91',
                        color: '#FFFFFF'
                      }}
                    >
                      {step.number}
                    </div>
                  </div>

                  {/* Step Content */}
                  <div className="max-w-[180px]">
                    <h3
                      className="text-base font-bold tracking-tight mb-1"
                      style={{ color: '#131B2E' }}
                    >
                      {step.title}
                    </h3>
                    <p
                      className="text-xs leading-relaxed"
                      style={{ color: '#475569' }}
                    >
                      {step.description}
                    </p>
                  </div>

                  {/* Arrow connector - Mobile/Tablet */}
                  {index < processSteps.length - 1 && (
                    <div 
                      className="lg:hidden mt-6 w-px h-8 sm:hidden"
                      style={{ backgroundColor: '#E2E8F0' }}
                    />
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Bottom CTA */}
          <div 
            className="mt-6 text-center"
            data-testid="process-cta"
          >
            <p
              className="text-base mb-4"
              style={{ color: '#475569' }}
            >
              Ready to see results in 90 days?
            </p>
            <button
              onClick={onOpenContactModal}
              className="kv-btn-primary px-7 py-3 text-base"
              data-testid="process-contact-cta"
            >
              Book a Strategy Call
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ProcessSection;
